<template>
  <div class="flex-1 min-h-0 flex flex-col">
    <ComponentHeader title="Kairon Intelligence" :heading-level="4">
      <button
        v-if="isSuperUser"
        type="button"
        class="px-3 py-2 rounded text-[13px] font-medium text-gray-600 hover:bg-growth-25 hover:text-gray-900 transition-colors disabled:cursor-not-allowed"
        :disabled="submitting"
        title="Superuser: queue this week's engagement report"
        @click="submitReport"
      >
        Generate weekly report
      </button>
    </ComponentHeader>

    <div class="flex-1 min-h-0 flex flex-col gap-5 p-5">
      <div class="shrink-0">
        <AnalystQueryForm
          ref="queryForm"
          :loading="submitting"
          @submit="handleSubmit"
        />
        <div v-if="errorMessage" class="mt-4 px-4 py-2 bg-red-tint border border-red-light rounded">
          <p class="text-[13px] text-red-dark">{{ errorMessage }}</p>
        </div>
      </div>

      <div class="flex-1 min-h-0 flex flex-col">
        <div class="shrink-0 flex items-center justify-between gap-4 mb-2">
          <h3 class="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">Recent</h3>
          <div v-if="isSuperUser" class="inline-flex rounded border border-gray-200 overflow-hidden text-[11px] font-medium">
            <button
              v-for="option in scopeOptions"
              :key="option.value"
              type="button"
              class="px-2 py-1 border-l border-gray-200 first:border-l-0 transition-colors"
              :class="scope === option.value ? 'bg-growth-25 text-gray-900' : 'bg-white text-gray-600 hover:text-gray-900'"
              @click="setScope(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
        <AnalystHistory
          class="flex-1 min-h-0"
          :jobs="historyJobs"
          :loading="loadingJobs"
          :active-job-id="activeJob?.id"
          :show-user="scope === 'everyone'"
          :page="page"
          :page-count="pageCount"
          @select="handleSelectJob"
          @page-change="goToPage"
        />
      </div>
    </div>

    <AnalystResultModal
      v-if="showResultModal && activeJob"
      :job="activeJob"
      :polling="polling"
      :regenerating="regenerating"
      @follow-up="handleFollowUp"
      @feedback-changed="handleFeedbackChanged"
      @regenerate="handleRegenerate"
      @close="showResultModal = false"
    />
  </div>
</template>

<script>
import AnalystQueryForm from './AnalystQueryForm.vue'
import AnalystHistory from './AnalystHistory.vue'
import AnalystResultModal from './AnalystResultModal.vue'
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'
import { bulkSyncJobs, createJob, getJob, listJobs } from '@/services/api/personalAnalyst'

const PANEL_JOB_TYPES = ['personal_analyst', 'weekly_report']
const PAGE_SIZE = 50

export default {
  name: 'AnalystPanel',
  components: {
    AnalystQueryForm,
    AnalystHistory,
    AnalystResultModal,
    ComponentHeader,
  },
  data() {
    return {
      jobs: [],
      page: 0,
      totalJobs: 0,
      activeJob: null,
      loadingJobs: false,
      submitting: false,
      polling: false,
      pollInterval: null,
      errorMessage: null,
      showResultModal: false,
      regenerating: false,
      scope: 'mine',
      scopeOptions: [
        { value: 'mine', label: 'Mine' },
        { value: 'everyone', label: 'Everyone' },
      ],
    }
  },
  computed: {
    isSuperUser() {
      return this.$store.getters['auth/current_user']?.is_superuser || false
    },
    currentUserId() {
      return this.$store.getters['auth/current_user']?.id
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.totalJobs / PAGE_SIZE))
    },
    historyJobs() {
      const seen = new Set()
      return this.jobs.filter(job => {
        const key = `${job.user}|${job.job_type}|${job.query.trim().toLowerCase()}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },
  },
  mounted() {
    this.fetchJobs()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    async fetchJobs() {
      this.loadingJobs = true
      try {
        const data = await listJobs({
          entity__isnull: true,
          job_type__in: PANEL_JOB_TYPES,
          // "Mine" = own jobs plus shared weekly reports; only superusers can see more.
          mine: this.scope === 'mine',
          limit: PAGE_SIZE,
          offset: this.page * PAGE_SIZE,
        })
        this.jobs = data.results
        this.totalJobs = data.count
      } catch (err) {
        this.errorMessage = 'Failed to load query history.'
        return
      } finally {
        this.loadingJobs = false
      }
      await this.syncPendingJobs()
    },

    goToPage(page) {
      this.page = page
      this.fetchJobs()
    },

    setScope(scope) {
      if (scope === this.scope) return
      this.scope = scope
      this.goToPage(0)
    },

    async syncPendingJobs() {
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) return

      try {
        this.mergeUpdatedJobs(await bulkSyncJobs({ entity__isnull: true }))
      } catch {
        // Sync failure is non-fatal — list still rendered, polling will retry.
      }

      // If any are still pending after sync, start polling
      const stillPending = this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')
      if (stillPending) {
        if (!this.activeJob) this.activeJob = this.jobs.find(j => j.status === 'QUEUED' || j.status === 'PROCESSING')
        this.startPolling()
      }
    },

    mergeUpdatedJobs(updated) {
      for (const job of updated) {
        const idx = this.jobs.findIndex(j => j.id === job.id)
        if (idx !== -1) this.jobs.splice(idx, 1, job)
        if (this.activeJob?.id === job.id) this.activeJob = job
      }
    },

    async selectJob(job) {
      this.errorMessage = null
      this.activeJob = job

      // Immediately fetch latest state via retrieve endpoint (triggers lazy sync)
      try {
        const updatedJob = await getJob(job.id)
        this.activeJob = updatedJob

        const idx = this.jobs.findIndex(j => j.id === updatedJob.id)
        if (idx !== -1) {
          this.jobs.splice(idx, 1, updatedJob)
        }

        if (updatedJob.status === 'QUEUED' || updatedJob.status === 'PROCESSING') {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      } catch {
        // Fall back to list data if retrieve fails
        if (job.status === 'QUEUED' || job.status === 'PROCESSING') {
          this.startPolling()
        } else {
          this.stopPolling()
        }
      }
    },

    async handleSubmit(formData) {
      this.submitting = true
      this.errorMessage = null
      try {
        const payload = { query: formData.query }
        if (formData.job_type) {
          payload.job_type = formData.job_type
        }
        const newJob = await createJob(payload)
        this.jobs.unshift(newJob)
        this.activeJob = newJob
        this.startPolling()
      } catch (err) {
        if (err.response?.status === 429) {
          this.errorMessage = err.response.data.detail
        } else {
          this.errorMessage = 'Failed to submit query. Please try again.'
        }
      } finally {
        this.submitting = false
      }
    },

    handleSelectJob(job) {
      this.selectJob(job)
      this.showResultModal = true
    },

    submitReport() {
      this.handleSubmit({
        query: 'Generate weekly engagement report',
        job_type: 'weekly_report',
      })
    },

    handleFollowUp(suggestion) {
      this.$refs.queryForm.appendQuery(suggestion)
    },

    handleFeedbackChanged(feedbackItems) {
      if (this.activeJob) {
        this.activeJob = { ...this.activeJob, feedback: feedbackItems }
        const idx = this.jobs.findIndex(j => j.id === this.activeJob.id)
        if (idx !== -1) {
          this.jobs.splice(idx, 1, this.activeJob)
        }
      }
    },

    async handleRegenerate() {
      if (!this.activeJob || this.regenerating) return
      this.regenerating = true
      try {
        const newJob = await createJob({
          job_type: this.activeJob.job_type,
          entity: this.activeJob.entity,
        })
        this.jobs.unshift(newJob)
        this.activeJob = newJob
        this.startPolling()
      } catch (err) {
        if (err.response?.status === 429) {
          this.errorMessage = err.response.data.detail
        } else {
          this.errorMessage = 'Failed to regenerate. Please try again.'
        }
      } finally {
        this.regenerating = false
      }
    },

    startPolling() {
      this.stopPolling()
      this.polling = true
      this.pollInterval = setInterval(() => this.pollActiveJob(), 30000)
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
      this.polling = false
    },

    async pollActiveJob() {
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) {
        this.stopPolling()
        return
      }

      try {
        this.mergeUpdatedJobs(await bulkSyncJobs({ entity__isnull: true }))
      } catch {
        // Non-fatal — try again next tick.
      }

      // Stop polling if all done
      if (!this.jobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')) {
        this.stopPolling()
      }
    },
  },
}
</script>
